import React from 'react';
import Switch from '../../ui/Switch';
import Checkbox from '../../ui/Checkbox';
import HelpButton from '../../ui/HelpButton';
import {
    DEFAULTS_CHANNEL_COLUMNS,
    DEFAULTS_CHANNEL_LABELS,
    availableChannels,
    notificationHelp,
} from '../../../lib/notificationPreferencesDefaults';
import './notificationDefaults.css';

export const CRITICAL_NOTE = 'Critical alerts always send, even when a channel is off.';

// One notification row: the company-wide Switch, its name, "Off for <Company>" when off, then one Checkbox per
// channel ("—" where the channel isn't available). One combined help article lives in the group header.
function NotificationDefaultsRow({ row, company, busy, onToggleSwitch, onToggleChannel }) {
    const { workflow, enabled, switchPending, channels, pendingChannels, critical, seniorNudge } = row;
    const stateId = `nd-state-${workflow.workflowKey}`;
    return (
        <tr data-testid="nd-row" data-event-type={workflow.eventType} data-company-switch={enabled ? 'on' : 'off'} className="nd-row">
            <th scope="row" className="nd-name-cell">
                <div className="nd-name">
                    <Switch
                        checked={enabled}
                        showState={false}
                        busy={switchPending}
                        disabled={busy}
                        label={`${workflow.displayName} for ${company}`}
                        describedBy={enabled ? undefined : stateId}
                        onChange={() => onToggleSwitch(workflow.eventType)}
                    />
                    <span className="nd-title">{workflow.displayName}</span>
                    {/* Board 04 / BUILD_PROMPT: only a switched-off row says so ("Off for The Ride"); on rows add nothing. */}
                    {!enabled && (
                        <span id={stateId} data-testid="nd-company-state" className="nd-state">{`Off for ${company}`}</span>
                    )}
                </div>
                {critical && <p data-testid="critical-note" className="nd-note">{CRITICAL_NOTE}</p>}
                {seniorNudge && (
                    <p data-testid="senior-nudge" className="nd-note">
                        Push only: some of your clients are seniors, so consider also allowing SMS or Voice.
                    </p>
                )}
            </th>
            {DEFAULTS_CHANNEL_COLUMNS.map((channel) => (
                <td key={channel} className="nd-cell">
                    {availableChannels(workflow).includes(channel) ? (
                        <Checkbox
                            aria-label={`${workflow.displayName}: ${DEFAULTS_CHANNEL_LABELS[channel]}`}
                            checked={channels.includes(channel)}
                            disabled={!enabled || busy}
                            className={pendingChannels.includes(channel) ? 'animate-pulse' : ''}
                            onChange={() => onToggleChannel(workflow.eventType, channel)}
                        />
                    ) : (
                        <span className="nd-na" aria-label={`${DEFAULTS_CHANNEL_LABELS[channel]} not available`} role="img">—</span>
                    )}
                </td>
            ))}
        </tr>
    );
}

export default function NotificationDefaultsGroup({ label, rows, company, busy, onToggleSwitch, onToggleChannel }) {
    if (!rows.length) return null;
    return (
        <section className="nd-group" aria-label={label} data-testid="nd-group">
            <div className="ia-d2-scroll">
                <table className="nd-table">
                    <thead>
                        <tr>
                            <th scope="col" className="nd-group-head">{label}
                                <HelpButton title={`${label} notifications`}>
                                    <span className="block">The switch turns a notification on or off for the company. The checkboxes choose its channels. Critical alerts always send.</span>
                                    {rows.map(({ workflow }) => {
                                        const help = notificationHelp(workflow);
                                        return <span key={workflow.workflowKey} className="mt-3 block"><strong>{workflow.displayName}</strong>
                                            <span className="block">{help.what}</span>
                                            <span className="block"><strong>Who gets it:</strong> {help.who}</span>
                                            <span className="block"><strong>When it’s sent:</strong> {help.when}</span>
                                        </span>;
                                    })}
                                </HelpButton>
                            </th>
                            {DEFAULTS_CHANNEL_COLUMNS.map((channel) => (
                                <th key={channel} scope="col" className="nd-col-head">{DEFAULTS_CHANNEL_LABELS[channel]}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <NotificationDefaultsRow
                                key={row.workflow.workflowKey}
                                row={row}
                                company={company}
                                busy={busy}
                                onToggleSwitch={onToggleSwitch}
                                onToggleChannel={onToggleChannel}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
